import { isNotNullish } from '@adornis/assert/isNullish.js';
import { useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { getAllInternalMartinusSchools } from '../../groups/api/getAllSchools.js';
import { getSchoolIDsForRoles } from '../../permissions/api/getSchoolIDsForRoles.js';
import { hasRoles } from '../../permissions/api/hasRoles.js';
import { hashUserPermissionFields } from '../../permissions/hashUserPermissionFields.js';
import { Role } from '../../permissions/roles.js';
import type { Person } from '../../users/entities/person.js';

/** Willigis Gymnasium and Realschule book onto one shared Mandant — the Gymnasium's. */
const WILLIGIS_GYMNASIUM = 'Willigis Gymnasium Mainz';
const WILLIGIS_REALSCHULE = 'Willigis Realschule Mainz';
const WILLIGIS_MANDANT = 'Willigis';

/**
 * The roles the Buchhaltung pages are scoped by: both are bound to their schools, so they only ever
 * get the Mandanten of the schools their role sits on. A globally scoped role (a central
 * Buchhaltung, an Admin) is not bound to any school and gets all of them.
 */
const ACCOUNTING_SCHOOL_ROLES = [Role.BUCHHALTUNG, Role.SCHULLEITER];

export interface AccountingMandant {
  _id: string;
  name: string;
  /** the Buchhaltungsmandant of the school — what its accounting entries carry as `mandant` */
  accountingNumber: string;
  /** the schools whose role grants this Mandant — and whose ids its entries reference */
  grantedBySchoolIDs: string[];
}

/**
 * The Mandanten of the Buchhaltung the current user may see: one per school with a
 * Mandantennummer, except Willigis — the Realschule is no Mandant of its own, so it is folded into
 * the Gymnasium's, and a role on either of the two schools grants that shared Mandant. `schoolIDs`
 * are all their schools — the perspective of the open items and the matching review.
 */
export const useAccountingMandanten = (): {
  loading: boolean;
  mandanten: AccountingMandant[];
  schoolIDs: string[];
} => {
  const user = useObservable(() => currentUser<Person>(), []);
  const { value: schools, loading } = useQuery(() => getAllInternalMartinusSchools(), []);
  // a school role carries its schools in its contexts — those are the Mandanten the user may see
  const { value: schoolIDsOfUser, loading: schoolIDsLoading } = useQuery(
    () => getSchoolIDsForRoles(user, ACCOUNTING_SCHOOL_ROLES),
    [hashUserPermissionFields(user as Parameters<typeof hashUserPermissionFields>[0])],
  );

  // nothing is shown before the roles are known — an empty list would read as "no permission"
  if (!user || loading || schoolIDsLoading) return { loading: true, mandanten: [], schoolIDs: [] };

  // a global role is bound to no school, so it is not in any school's context — it sees every Mandant
  const seesAllMandanten =
    hasRoles({ user, roles: [Role.ADMIN], contexts: [GLOBAL_CONTEXT] }) ||
    hasRoles({ user, roles: ACCOUNTING_SCHOOL_ROLES, contexts: [GLOBAL_CONTEXT] });
  const willigisRealschuleID = schools?.find(school => school.name === WILLIGIS_REALSCHULE)?._id;

  // only schools with a Mandantennummer keep books; the Realschule is folded into the Gymnasium below
  const mandanten = (schools ?? [])
    .filter(school => school.accountingNumber && school.name !== WILLIGIS_REALSCHULE)
    .map(school =>
      school.name === WILLIGIS_GYMNASIUM
        ? {
            _id: school._id,
            name: WILLIGIS_MANDANT,
            accountingNumber: school.accountingNumber!,
            grantedBySchoolIDs: [school._id, willigisRealschuleID].filter(isNotNullish),
          }
        : {
            _id: school._id,
            name: school.name ?? '',
            accountingNumber: school.accountingNumber!,
            grantedBySchoolIDs: [school._id],
          },
    )
    .filter(
      mandant => seesAllMandanten || mandant.grantedBySchoolIDs.some(schoolID => schoolIDsOfUser?.includes(schoolID)),
    );

  return {
    loading: false,
    mandanten,
    // entries reference schools, not Mandanten — both Willigis schools must be in the perspective
    schoolIDs: [...new Set(mandanten.flatMap(mandant => mandant.grantedBySchoolIDs))],
  };
};
